import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager';
import i18n from '../../utils/i18n';
import {
  default as TextInput,
  InputUITypes,
  ValidationTypes
} from '@adsk/bim360-matrix-react-text-input';
import Button from '@adsk/bim360-matrix-react-button';
import Text from '@adsk/bim360-matrix-react-text';
import Toggle from '@adsk/bim360-matrix-react-toggle';
import {templateCategoryType} from '../../constants/proptype-validation';
import {useValueWithPristineFlag} from '../common/formHooks';
import {getIssueTemplateCategoryValidators} from '../../utils/issue-template-helpers';

const TemplateCategoryRow = ({
  category,
  templateCategoryTitles,
  container,
  updateTemplateCategoryAction,
  isLoading,
  onDeleteClicked,
  showNotificationAction
}) => {
  const [title, onChange, isPristine, resetField] = useValueWithPristineFlag(category.title);
  const [isActive, setIsActive] = useState(category.isActive);
  const updateFailedNotification = {
    type: NotificationTypes.TOAST,
    level: NotificationLevels.ERROR,
    message: i18n.t('templatesGrid.manageCategoriesModal.error.updateFailed')
  };

  const getUpdateSuccessNotification = ({title: categoryTitle}) => ({
    type: NotificationTypes.TOAST,
    level: NotificationLevels.SUCCESS,
    message: (
      <Text hasHTML>
        {i18n.t('templatesGrid.manageCategoriesModal.updateSuccess', {
          title: categoryTitle
        })}
      </Text>
    )
  });

  return (
    <div className="TemplateCategoryRow">
      <TextInput
        value={title}
        editableProps={{
          isLoading,
          className: 'TemplateCategoryRow__title-editable'
        }}
        inputUIType={InputUITypes.INLINE_EDIT}
        validationProps={{
          className: 'TemplateCategoryRow__title-validation',
          type: ValidationTypes.POPOVER,
          validator: getIssueTemplateCategoryValidators(templateCategoryTitles),
          ignore: isPristine || category.title === title,
          required: true,
          typeProps: {
            container
          }
        }}
        inputProps={{
          className: 'TemplateCategoryRow__title-text-field',
          value: title,
          autoselect: true,
          onChange,
          onInlineEditEnd: (value, isCancel) => {
            if (!isCancel && value !== category.title) {
              updateTemplateCategoryAction(
                category.id,
                {title: value},
                {
                  onSuccess: () => {
                    resetField();
                    showNotificationAction(getUpdateSuccessNotification({title: value}));
                  },
                  onError: () => {
                    resetField();
                    showNotificationAction(updateFailedNotification);
                  }
                }
              );
            }
          }
        }}
      />
      <Toggle
        className="TemplateCategoryRow__is-active"
        checked={isActive}
        disabled={isLoading}
        onChange={value => {
          setIsActive(value);
          updateTemplateCategoryAction(
            category.id,
            {isActive: value},
            {
              onSuccess: () => {
                showNotificationAction(getUpdateSuccessNotification({title: category.title}));
              },
              onError: () => {
                setIsActive(category.isActive);
                showNotificationAction(updateFailedNotification);
              }
            }
          );
        }}
      />
      <Button
        styleType={Button.StyleType.LINK_SECONDARY}
        className="TemplateCategoryRow__remove"
        disabled={isLoading}
        onClick={() => {
          onDeleteClicked(category);
        }}
      >
        {i18n.t('delete')}
      </Button>
    </div>
  );
};

TemplateCategoryRow.displayName = 'TemplateCategoryRow';

TemplateCategoryRow.propTypes = {
  category: PropTypes.shape(templateCategoryType).isRequired,
  templateCategoryTitles: PropTypes.arrayOf(templateCategoryType).isRequired,
  container: PropTypes.object,
  updateTemplateCategoryAction: PropTypes.func.isRequired,
  onDeleteClicked: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  showNotificationAction: PropTypes.func.isRequired
};

export default TemplateCategoryRow;
